import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clapperboard,
  Cpu,
  Crown,
  Grid2x2,
  Landmark,
  Layers,
  ShieldCheck,
} from "lucide-react";
import { useContactModal } from "@/context/ContactModalContext";
import CTABanner from "@/components/CTABanner";
import Reveal from "@/components/Reveal";
import { SERVICES, INDUSTRIES } from "@/lib/content";
import heroImage from "@/assets/images/sable/hero.jpg";
import boardroomImage from "@/assets/images/sable/boardroom.jpg";

const ICONS = {
  strategy: Crown,
  regulatory: ShieldCheck,
  digital: null,
  transactions: Layers,
};

const INDUSTRY_ICONS = {
  Technology: Cpu,
  "Media & Entertainment": Clapperboard,
  "Digital Platforms": Grid2x2,
  "Investment & Private Equity": Landmark,
};

const Home = () => {
  const { open } = useContactModal();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const bgTranslate = scrollY * 0.25;
  const contentTranslate = Math.min(scrollY * -0.12, 0);
  const contentOpacity = Math.max(1 - scrollY / 420, 0);
  const scrollIndicatorOpacity = Math.max(1 - scrollY / 360, 0);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[110vh] 2xl:min-h-[900px] overflow-hidden"
      >
        <div className="relative min-h-[110vh] 2xl:min-h-[900px] overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              transform: `translate3d(0, ${bgTranslate}px, 0) scale(1.08)`,
              willChange: "transform",
            }}
          >
            <img
              src={heroImage}
              alt="City skyline at dusk"
              className="w-full h-full object-cover object-[center_35%] brightness-[1.08] contrast-[1.03]"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/65 to-[#0A0A0A]/20" />
            <div className="absolute inset-0 bg-[#0A0A0A]/10" />
          </div>

          <div
            className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 min-h-[110vh] 2xl:min-h-[900px]"
            style={{
              transform: `translate3d(0, ${contentTranslate}px, 0)`,
              opacity: contentOpacity,
              willChange: "transform, opacity",
            }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[110vh] 2xl:min-h-[900px]">
              {/* Left text */}
              <div className="lg:col-span-7 py-16 lg:py-24 flex flex-col justify-center lg:-translate-y-10 fade-up">
                <h1 className="font-serif text-[42px] sm:text-[52px] lg:text-[68px] leading-[1.04] text-offwhite tracking-[-0.015em] max-w-[15ch]">
                  Strategic advisory for companies navigating complex markets.
                </h1>
                <p className="mt-10 max-w-[46ch] text-[15px] md:text-[16px] leading-[1.7] text-offwhite/65">
                  We work with growth-stage companies and investors to navigate
                  regulation, expansion, and complex commercial decisions.
                </p>

                <div className="mt-12 flex flex-wrap items-center gap-8">
                  <Link
                    to="/services"
                    data-testid="hero-cta-services"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-gold border border-gold/20 text-[#0A0A0A] text-[13.5px] tracking-[0.05em] font-medium hover:bg-[#D9BC85] transition-colors"
                  >
                    Our Services
                    <ArrowRight className="hover-arrow" size={16} />
                  </Link>
                  <Link
                    to="/about"
                    data-testid="hero-cta-about"
                    className="link-underline text-[14px] text-offwhite/85"
                  >
                    Learn more about us
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div
            className="hero-scroll-indicator scroll-indicator-fade"
            style={{ opacity: scrollIndicatorOpacity }}
            aria-hidden="true"
          >
            <span className="hero-scroll-label">SCROLL</span>
            <span className="hero-scroll-line">
              <span className="hero-scroll-bar" />
            </span>
          </div>
        </div>

      </section>

      {/* SERVICES OVERVIEW */}
      <section
        data-testid="home-services"
        className="relative z-20 bg-deep border-t border-hairline lg:-mt-16"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14">
            {SERVICES.map((s, idx) => {
              const Icon = ICONS[s.key];
              return (
                <Reveal key={s.key} delay={idx * 90}>
                  <Link
                    to="/services"
                    data-testid={`home-service-${s.key}`}
                    className={`group relative block ${idx > 0 ? "lg:pl-8" : ""}`}
                  >
                    {idx > 0 && (
                      <span className="hidden lg:block absolute left-0 top-0 h-full w-px bg-[linear-gradient(to_bottom,transparent,rgba(212,182,111,0.16),transparent)]" />
                    )}
                    {s.key === "digital" ? (
                      <div className="mb-8 grid grid-cols-2 gap-1.5 w-[34px] h-[34px] drop-shadow-[3px_4px_8px_rgba(198,169,107,0.18)]">
                        {[0, 1, 2, 3].map((i) => (
                          <span
                            key={i}
                            className={`border border-gold bg-gold/30 ${i === 1 || i === 2 ? "translate-x-0.5 translate-y-0.5" : ""}`}
                          />
                        ))}
                      </div>
                    ) : (
                      <Icon
                        size={34}
                        strokeWidth={1.25}
                        className="text-gold mb-8"
                      />
                    )}
                    <h3 className="font-serif text-[24px] text-[#D4B66F] group-hover:text-gold transition-colors mb-4">
                      {s.title}
                    </h3>
                    <p className="text-[13.5px] leading-[1.65] text-[#8A8A8A] group-hover:text-white/70 transition-colors max-w-[28ch]">
                      {s.short}
                    </p>
                    <div className="mt-5 inline-flex items-center text-white/78 group-hover:text-gold transition-colors">
                      <svg
                        width="34"
                        height="18"
                        viewBox="0 0 34 18"
                        fill="none"
                        aria-hidden="true"
                        className="group-hover:translate-x-1 transition-transform"
                      >
                        <path
                          d="M2 9H31M26.5 4.5L31 9L26.5 13.5"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* POSITIONING + INDUSTRIES */}
      <section
        data-testid="home-positioning"
        className="border-t border-hairline bg-[#0B0B0B]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-12">
            {/* Focus */}
            <Reveal className="lg:col-span-4 lg:pr-8">
              <span className="eyebrow">Our focus</span>
              <h2 className="mt-6 font-serif text-[34px] lg:text-[40px] leading-[1.15] text-offwhite">
                We work with companies operating in fast-moving and highly
                regulated environments.
              </h2>
              <p className="mt-8 text-[14.5px] leading-[1.75] text-[#8A8A8A] max-w-[42ch]">
                From early-stage growth to complex transactions, we provide
                clear, strategic guidance where it matters most.
              </p>
            </Reveal>

            {/* Industries list */}
            <Reveal className="lg:col-span-4 lg:px-8 lg:border-l lg:border-hairline" delay={120}>
              <span className="eyebrow">Industries we serve</span>
              <ul className="mt-2">
                {INDUSTRIES.map((i, idx) => {
                  const IndustryIcon = INDUSTRY_ICONS[i.name];
                  return (
                  <li
                    key={i.name}
                    data-testid={`industry-${i.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="relative flex items-start gap-5 group py-6"
                  >
                    {idx < INDUSTRIES.length - 1 && (
                      <span className="absolute left-0 right-0 bottom-0 h-px bg-[linear-gradient(to_right,transparent,rgba(212,182,111,0.14),transparent)]" />
                    )}
                    <IndustryIcon
                      size={26}
                      strokeWidth={1.45}
                      className="mt-1 shrink-0 text-[#D4B66F]"
                    />
                    <div>
                      <p className="text-[16px] text-offwhite/90 group-hover:text-gold transition-colors">
                        {i.name}
                      </p>
                      <p className="mt-0.5 text-[13px] text-[#8A8A8A]">
                        {i.description}
                      </p>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </Reveal>

            {/* Image */}
            <Reveal className="lg:col-span-4 lg:pl-4" delay={240}>
              <div className="relative aspect-[5/6] w-full overflow-hidden">
                <img
                  src={boardroomImage}
                  alt="Boardroom overlooking the city"
                  className="w-full h-full object-cover object-[62%_center]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/25" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CREDIBILITY */}
      <section
        data-testid="home-credibility"
        className="border-t border-hairline"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-5">
              <span className="eyebrow">A different kind of advisor</span>
              <h2 className="mt-6 font-serif text-[34px] lg:text-[42px] leading-[1.12] text-offwhite">
                Independent, senior-led, and built around your decisions.
              </h2>
            </Reveal>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-10">
              {[
                {
                  k: "Senior",
                  t: "Senior-led engagement",
                  d: "Every engagement is led by partners with operating and advisory experience — no layered teams, no handoffs.",
                },
                {
                  k: "Independent",
                  t: "Genuinely independent",
                  d: "We provide unconflicted advice. Our incentives are aligned to outcomes, not products.",
                },
                {
                  k: "Outcomes",
                  t: "Built for outcomes",
                  d: "We focus on the few decisions that change the trajectory of a business, and the work needed to make them well.",
                },
              ].map((b, idx) => (
                <Reveal key={b.k} delay={idx * 120}>
                  <div className="h-px w-10 bg-gold mb-6" />
                  <h4 className="font-serif text-[20px] text-offwhite mb-3">
                    {b.t}
                  </h4>
                  <p className="text-[13.5px] leading-[1.75] text-offwhite/60">
                    {b.d}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default Home;
